import { Component, Input, OnInit, ViewChild } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatPaginator } from "@angular/material/paginator";
import { MatTableDataSource } from "@angular/material/table";
import { DomSanitizer, SafeHtml } from "@angular/platform-browser";
import { Router } from "@angular/router";
import * as moment from "moment";
import Swal from "sweetalert2";
import { DataService } from "../../../services/data.service";
import { HvService } from "../../../services/hv.service";
import { EpackEolDialogComponent } from "../epack-dialouge/epack-dialouge.component";
import {
  EpackPdfReportComponent,
  IndividualTableData,
} from "../epack-pdf-report/epack-pdf-report.component";

interface FileData {
  user: string;
  metaData: MetaData;
}

interface MetaData {
  test_date: string;
  timestamp: number;
  battery_unique_key: number;
  bms_id: number;
  fileUrl: string;
  fileName: string;
}

interface EpackEolDataWithTestRig {
  test_rig_number: string;
  test_id: string;
  pack_id: string;
  test_stage: number;
  test_status: string;
  user_id: string;
  fileData: FileData;
  bms_id: string;
  created_at: string;
  updated_at: string;
  test_end_time: string;
  final_eol_result: string;
}

interface EpackEolTableData {
  "S.No": number;
  "Test Status": string;
  "Rig No"?: string;
  "Test ID": string;
  "Start Date": string;
  "End Date"?: string;
  "Battery ID": string | number;
  "BMS ID": string | number;
  fileName: string;
  "Test Stage"?: string;
  final_eol_result?: string;
}

@Component({
  selector: "app-hv-epack-dashboard-table",
  templateUrl: "./hv-epack-dashboard-table.component.html",
  styleUrls: ["./hv-epack-dashboard-table.component.css"],
})
export class HvEpackDashboardTableComponent implements OnInit {
  constructor(
    private hvService: HvService,
    private dialog: MatDialog,
    private sanitizer: DomSanitizer,
    private router: Router,
    private dataService: DataService,
  ) {}

  @Input() packId: string;

  @ViewChild(MatPaginator) paginator: MatPaginator;
  // @ViewChild(EpackPdfReportComponent)
  // picoloEpackPdfReportComponent: EpackPdfReportComponent;
  @ViewChild(EpackPdfReportComponent)
  epackPdfReportComponent: EpackPdfReportComponent;

  hvEpackEolTableData: MatTableDataSource<EpackEolTableData> =
    new MatTableDataSource<EpackEolTableData>([]);
  tableIcons: {
    pt: string;
    ft: string;
    csv: string;
  } = {
    pt: "assets/img/epack-dashboard/PT.png",
    ft: "assets/img/epack-dashboard/FT.png",
    csv: "assets/img/epack-dashboard/CSV.png",
  };

  HvEpackEolTableHeaders: string[] = [
    "S.No",
    "Test ID",
    "Rig No",
    "Battery ID",
    "Test Stage",
    "Test Status",
    "Start Date",
    "End Date",
    "Analytics",
    "Download",
  ];

  ngOnInit(): void {
    this.getEpackEolDataByTestRig();
  }

  getEpackEolDataByTestRig() {
    this.hvService.getEolDataByTestRig().subscribe({
      next: (res) => {
        const responseBody = res.body as {
          success: boolean;
          message: string;
          data: EpackEolDataWithTestRig[];
        };

        if (responseBody.success) {
          this.setTableData(responseBody.data);
        } else {
          console.log(responseBody.message);
        }
      },
      error: (err) => {
        console.log(err);
      },
    });
  }

  setTableData(data: EpackEolDataWithTestRig[]) {
    const resData: EpackEolTableData[] = data.map((data, index) => {
      const {
        bms_id,
        final_eol_result,
        fileData,
        pack_id,
        test_id,
        test_rig_number,
        test_stage,
        test_status,
        user_id,
        created_at: test_date,
        test_end_time: end_date,
      } = data;

      const start_date_string = moment(test_date).format("DD-MM-YYYY");
      const end_date_string = end_date
        ? moment(end_date).format("DD-MM-YYYY")
        : "N/A";
      const fileExists = fileData?.metaData?.fileName ? true : false;
      return {
        "Battery ID": pack_id,
        "BMS ID": bms_id,
        "Start Date": start_date_string,
        "End Date": end_date_string,
        "Test ID": test_id,
        "S.No": index + 1,
        fileName: fileData?.metaData?.fileName,
        "Test Stage": fileExists ? "EOL Test Completed" : test_status,
        "Test Status": final_eol_result ?? "In Progress",
        "Rig No": test_rig_number,
      };
    });

    if (this.packId) {
      this.hvEpackEolTableData = new MatTableDataSource<EpackEolTableData>(
        resData
          .filter((_) => _["Battery ID"] === this.packId)
          .map((_, i) => ({ ..._, "S.No": i + 1 })),
      );
    } else {
      this.hvEpackEolTableData = new MatTableDataSource<EpackEolTableData>(
        resData,
      );
    }

    this.hvEpackEolTableData.paginator = this.paginator;
  }

  applyFilter(event: Event) {
    const filterValue = (event.target as HTMLInputElement).value;
    this.hvEpackEolTableData.filter = filterValue.trim().toLowerCase();

    if (this.hvEpackEolTableData.paginator) {
      this.hvEpackEolTableData.paginator.firstPage();
    }
  }

  async openDialog(
    testId: string,
    testDate: string,
    packId: string,
    bmsId: string,
    rigNum: string,
  ) {
    const testMetaData = await this.getGroupedMetaDataByTestid(testId);

    let ratedCapacity = "---";

    if (testMetaData?.dialougeData?.ratedCapacity) {
      const data: any = testMetaData?.dialougeData?.ratedCapacity;
      ratedCapacity = `${data.value} ${data.unit}`;
    }

    const headerData = {
      "Test ID": testId,
      "Test Date": testDate,
      "Battery Unique ID": packId,
      "BMS Unique ID": bmsId,
      "Hardware Version": testMetaData?.dialougeData?.hardware_version ?? "---",
      "Variant (assigned during EOL)":
        testMetaData?.dialougeData?.variant ?? "---",
      Product: testMetaData?.dialougeData?.product ?? "---",
      "Test Rig": rigNum,
      "Nominal voltage": "51.25 V",
      "Rated capacity": ratedCapacity,
    };

    const data = {
      headerData,
      content: this.generateGroupedMetaDataDialogData(testMetaData.data),
    };

    this.dialog.open(EpackEolDialogComponent, {
      height: "95%",
      width: "90%",
      maxWidth: "1250px",
      autoFocus: true,
      data,
    });
  }

  generateGroupedMetaDataDialogData(dataByMajorGroup: {
    [index: string]: string | number | null;
  }): SafeHtml {
    const majorGroups = Object.keys(dataByMajorGroup).filter(
      (group) => group !== "NA",
    );

    const mainDataContainer = document.createElement("section");
    mainDataContainer.id = "metaDataContainer";
    mainDataContainer.style.textAlign = "center";

    majorGroups.forEach((majorGroup) => {
      // creating major container
      const majorGroupContainer = document.createElement("section");
      majorGroupContainer.classList.add("majorGroupContianer");
      majorGroupContainer.style.backgroundColor = "#E4E2E2";
      majorGroupContainer.style.border = "1px solid #A3A3A3";
      majorGroupContainer.style.marginTop = "1.5rem";
      majorGroupContainer.style.paddingBottom = "1rem";

      const majorGroupHeader = document.createElement("h2");
      majorGroupHeader.classList.add("majorGroupHeader");
      majorGroupHeader.style.backgroundColor = "#BEBEBE";
      majorGroupHeader.style.padding = "0.5rem";
      majorGroupHeader.style.marginBottom = "1rem";
      majorGroupHeader.style.fontSize = "1.2rem";
      majorGroupHeader.style.margin = "0";

      majorGroupHeader.innerText = majorGroup;

      majorGroupContainer.appendChild(majorGroupHeader);

      // creating minor container
      const minorGroups = Object.keys(dataByMajorGroup[majorGroup]);
      minorGroups.forEach((minorGroup, index) => {
        const minorGroupContainer = document.createElement("section");
        minorGroupContainer.classList.add("minorSectionContainer");
        minorGroupContainer.style.backgroundColor = "#f1f1f1";
        minorGroupContainer.style.border = "1px solid #A3A3A3";
        minorGroupContainer.style.margin = "auto 0.3rem";
        minorGroupContainer.style.marginTop = "1.5rem";
        minorGroupContainer.style.boxShadow =
          "rgba(0, 0, 0, 0.35) 0px 5px 15px;";

        if (index === 0) {
          minorGroupContainer.style.marginTop = "0.5rem";
        }

        const minorGroupHeader = document.createElement("h2");
        minorGroupHeader.classList.add("minorGroupHeader");
        minorGroupHeader.style.backgroundColor = "#CFCFCF";
        minorGroupHeader.style.padding = "0.2rem";
        minorGroupHeader.style.fontSize = "1rem";
        minorGroupHeader.innerText = minorGroup;
        minorGroupHeader.style.margin = "0";
        minorGroupHeader.style.borderBottom = "1px solid #b5b5b5";
        minorGroupContainer.appendChild(minorGroupHeader);

        const metaDataContainer = document.createElement("section");
        // metadataContainer.classList.add("metaDataContainer");
        metaDataContainer.style.display = "grid";
        metaDataContainer.style.gridTemplateColumns = "repeat(4, 1fr)";
        metaDataContainer.style.gap = "0.5rem";
        metaDataContainer.style.padding = "0.6rem";

        const metaData = dataByMajorGroup[majorGroup][minorGroup];

        if (metaData.length > 0) {
          metaData.forEach((currentMetaData) => {
            const metaDataCard = document.createElement("div");
            const metaDataTitle = document.createElement("h4");
            const metaDataValue = document.createElement("p");

            metaDataTitle.innerText = Object.keys(currentMetaData)[0];
            metaDataValue.innerText = String(Object.values(currentMetaData)[0]);

            metaDataCard.style.display = "flex";
            metaDataCard.style.flexDirection = "column";
            metaDataCard.style.alignItems = "center";
            metaDataCard.style.justifyContent = "space-between";
            metaDataCard.style.border = "1.2px solid #A3A3A390";
            metaDataCard.style.backgroundColor = "#c7c7c7";
            metaDataCard.style.boxShadow = "rgba(0, 0, 0, 0.04) 0px 3px 5px;";

            metaDataTitle.style.padding = "0.5rem";
            metaDataTitle.style.margin = "0";

            metaDataValue.style.padding = "0.5rem";
            metaDataValue.style.margin = "0";
            metaDataValue.style.width = "100%";
            metaDataValue.style.backgroundColor = "#f1f1f1";
            metaDataValue.style.fontWeight = "500";

            if (
              String(Object.values(currentMetaData)[0]).trim().toLowerCase() ===
              "fail"
            ) {
              metaDataCard.style.border = "1.2px solid #B90000";
              metaDataCard.style.backgroundColor = "#ff2f3640";
              metaDataTitle.style.color = "#B90000";
              metaDataValue.style.color = "#B90000";
            }

            metaDataCard.appendChild(metaDataTitle);
            metaDataCard.appendChild(metaDataValue);
            metaDataContainer.appendChild(metaDataCard);
          });

          minorGroupContainer.appendChild(metaDataContainer);
        } else {
          const noDataContainer = document.createElement("p");
          noDataContainer.style.padding = "0.5rem";
          noDataContainer.style.margin = "0";
          noDataContainer.innerText = "No Data Found";
          noDataContainer.classList.add("noDataContainer");
          minorGroupContainer.appendChild(noDataContainer);
        }

        majorGroupContainer.appendChild(minorGroupContainer);
      });

      mainDataContainer.appendChild(majorGroupContainer);
    });

    const content = document.createElement("div");
    content.appendChild(mainDataContainer);

    return this.sanitizer.bypassSecurityTrustHtml(content.innerHTML);
  }

  getGroupedMetaDataByTestid(testId: string): Promise<{
    data: {
      [key: string]: string | number | null;
    };
    dialougeData: { [key: string]: string | number | null };
  }> {
    return new Promise((resolve) => {
      this.hvService.getGroupedMetaDataByTestid(testId).subscribe(
        (res) => {
          if (res.status === 200) {
            resolve({
              dialougeData: res.body.dialougeData,
              data: res.body.data,
            });
          } else {
            Swal.fire({
              icon: "error",
              text: res.body.message,
            });
          }
        },
        (err) => {
          console.log(err.message);
        },
      );
    });
  }

  renderRawDataGraph(testId: string, testDate: string) {
    if (!this.validateTestCompletion(testId)) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No test data available. Test must be completed to view raw data",
      });
      return;
    }

    // set data to local storage
    const payload = { testId, testDate };
    localStorage.setItem("epackRawDataInfo", JSON.stringify(payload));
    this.router.navigate(["eol/raw-data"]);
  }

  renderAnalyticsGrpah(testId: string, testDate: string) {
    if (!this.validateTestCompletion(testId)) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "No test data available. Test must be completed to view analytics",
      });
      return;
    }

    const splitedDate = testDate.split("-");

    const year = splitedDate[2];
    const month = splitedDate[1];
    const date = splitedDate[0];
    const fullDate = new Date(`${year}-${month}-${date}`);

    localStorage.setItem(
      "analyticsRawDataInfo",
      JSON.stringify({
        testId,
        year,
        month,
        date,
        fullDate,
      }),
    );

    this.router.navigate(["eol/graph"]);
  }

  validateTestCompletion(testId: string): boolean {
    const testData = this.hvEpackEolTableData.data.find(
      (_) => _["Test ID"] === testId,
    );

    if (!testData) {
      return false;
    }

    const testStatus = testData["Test Status"].trim().toLowerCase();
    const isTestCompleted = testStatus === "pass" || testStatus === "fail";

    return isTestCompleted;
  }

  downloadPreTestPdfReport(testId: string) {
    this.dataService
      .getpreTestPdfPresignedUrl(testId)
      .subscribe((res: { success: boolean; data: string; message: string }) => {
        if (res.success) {
          if (res.data) {
            // opens direct download link
            window.location.href = res.data;
          } else {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: res.message ?? "",
            });
          }
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: res.message ?? "",
          });
        }
      });
  }

  downloadEpackEolFile(fileName: string, testId: string) {
    this.dataService.downloadExponentEpackEOLFile(fileName, testId).subscribe(
      (res: any) => {
        if (res.status) {
          if (!res.result) {
            Swal.fire({
              icon: "error",
              title: "Error",
              text: "No file found for selected test",
            });
          } else {
            window.location.href = res.result;
          }
        }
      },
      (err) => {
        console.log("ERR: ", err);
      },
    );
  }

  downloadEpackPdfReport(testId: string) {
    this.hvService.getFinalReportByTestId(testId).subscribe({
      next: (res) => {
        if (res.status === 200) {
          const { metaData, summaryData, page1, page2 } = res.body?.data?.data;

          if (metaData["Test Date"] && metaData["Test Date"] !== "N/A") {
            const date = metaData["Test Date"];
            metaData["Test Date"] = moment(new Date(date)).format("DD-MM-YYYY");
          }

          const formatSummaryData = (
            summaryData: Record<string, string>,
          ): { [key: string]: { dataValue: string } } => {
            const result = {};

            Object.entries(summaryData).forEach(([key, val]) => {
              result[key] = { dataValue: val };
            });

            return result;
          };

          this.epackPdfReportComponent.downloadReport({
            dutDetailsData: metaData,
            summaryData: formatSummaryData(summaryData),
            page1TableData: page1,
            page2TableData: page2,
            battery_type: "hv",
          });
        }
      },
      error: (err) => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Report not found for requested test.",
        });
      },
    });
  }
}
